import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ScrollableTabsNavProps {
  /** Classes adicionais aplicadas no container externo (ex.: "border-b border-gray-200") */
  className?: string;
  /** O `<nav>` (ou qualquer conteúdo) que será scrollado horizontalmente */
  children: React.ReactNode;
  /** Quantidade de pixels a scrollar por clique nas setas (default 200) */
  scrollAmount?: number;
}

/**
 * Wrapper para menus de abas com scroll horizontal.
 *
 * Mostra setinhas (com gradiente fade) nas bordas quando há conteúdo
 * fora da viewport para indicar ao usuário que existem mais opções para
 * navegar. As setinhas são clicáveis e fazem scroll suave.
 *
 * Uso:
 * <ScrollableTabsNav className="border-b border-gray-200">
 *   <nav className="flex gap-3 ...">{tabs}</nav>
 * </ScrollableTabsNav>
 */
const ScrollableTabsNav: React.FC<ScrollableTabsNavProps> = ({
  className = '',
  children,
  scrollAmount = 200,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Tolerância de 2px para evitar flicker em rendering com sub-pixels
    const TOLERANCE = 2;
    setCanScrollLeft(el.scrollLeft > TOLERANCE);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - TOLERANCE);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();

    el.addEventListener('scroll', updateScrollState, { passive: true });

    // Observa mudanças no tamanho do conteúdo (ex.: tabs adicionadas dinamicamente)
    // ou do container (resize da viewport)
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateScrollState());
      resizeObserver.observe(el);
      if (el.firstElementChild) {
        resizeObserver.observe(el.firstElementChild);
      }
    }

    window.addEventListener('resize', updateScrollState);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [updateScrollState]);

  const scrollBy = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <div className={`relative min-w-0 ${className}`}>
      {/* Seta esquerda */}
      {canScrollLeft && (
        <>
          {/* Gradiente fade da esquerda (puramente visual) */}
          <div
            className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-r from-white via-white/80 to-transparent"
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={() => scrollBy('left')}
            aria-label="Ver opções anteriores"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-7 h-7 rounded-full bg-white border border-gray-200 shadow-md text-gray-700 hover:text-blue-600 hover:border-blue-300 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </>
      )}

      {/* Conteúdo scrollável (sem barra de rolagem visível, mas funcional) */}
      <div
        ref={scrollRef}
        className="overflow-x-auto scrollbar-hide"
        style={{
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge legado
        }}
      >
        {children}
      </div>

      {/* Seta direita */}
      {canScrollRight && (
        <>
          <div
            className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-l from-white via-white/80 to-transparent"
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={() => scrollBy('right')}
            aria-label="Ver mais opções"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-7 h-7 rounded-full bg-white border border-gray-200 shadow-md text-gray-700 hover:text-blue-600 hover:border-blue-300 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
};

export default ScrollableTabsNav;
